export function flattenObject(obj, parentKey = '', result = {}) {
  for (const [key, value] of Object.entries(obj)) {
    const newKey = parentKey ? `${parentKey}.${key}` : key
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenObject(value, newKey, result)
    } else {
      // If value is null, we still assign it; only truly skip 'undefined'.
      result[newKey] = value
    }
  }
  return result
}

export function invertFlatMapping(flatMap) {
  const invertedMap = {}
  for (const [key, value] of Object.entries(flatMap)) {
    if (typeof value === 'string') {
      invertedMap[value] = key
    }
  }
  return invertedMap
}

export function flattenMapping(map, parentKey = '', result = {}) {
  if (Array.isArray(map)) {
    if (map.length > 0) {
      // Process the first item in the array
      flattenMapping(map[0], `${parentKey}[]`, result)
    }
  } else if (map !== null && typeof map === 'object') {
    for (const [key, value] of Object.entries(map)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key
      flattenMapping(value, newKey, result)
    }
  } else {
    // Leaf node (could be null, number, string, etc.)
    result[parentKey] = map
  }
  return result
}

// Updated getValueAtPath
function getValueAtPath(obj, pathParts) {
  // If the current object is strictly undefined, we return undefined.
  if (obj === undefined) return undefined
  // If it's null, propagate null (instead of treating it like "not found").
  if (obj === null) return null

  if (pathParts.length === 0) {
    return obj
  }

  const part = pathParts[0]

  if (part === '[]') {
    if (!Array.isArray(obj)) return undefined
    return obj.map((item) => getValueAtPath(item, pathParts.slice(1)))
  } else {
    const nextObj = obj[part]
    if (nextObj === undefined) {
      return undefined
    }
    // If the next node is explicitly null, we want to return null
    if (nextObj === null) {
      return null
    }
    if (Array.isArray(nextObj)) {
      return nextObj.map((item) => getValueAtPath(item, pathParts.slice(1)))
    } else {
      return getValueAtPath(nextObj, pathParts.slice(1))
    }
  }
}

// Updated setValueAtPath
function setValueAtPath(obj, pathParts, value) {
  if (pathParts.length === 0) return

  const part = pathParts[0]

  if (part === '[]') {
    // If the target value is null, we do not attempt to treat it as an array
    if (value === null || !Array.isArray(value)) return
    // Ensure obj itself is an array reference; if it's null, replace it
    if (!Array.isArray(obj)) {
      // If obj was null or undefined, we need to replace it with an array.
      // However, since `obj` is passed by reference, we cannot reassign the
      // parent variable here—ensure that callers always initialize to {} or []
      return
    }
    for (let i = 0; i < value.length; i++) {
      if (obj[i] === undefined || obj[i] === null) {
        obj[i] = {}
      }
      setValueAtPath(obj[i], pathParts.slice(1), value[i])
    }
  } else {
    if (pathParts.length === 1) {
      obj[part] = value
    } else {
      // If next part is an array indicator
      if (pathParts[1] === '[]') {
        if (obj[part] === null || obj[part] === undefined || !Array.isArray(obj[part])) {
          obj[part] = []
        }
      } else {
        if (obj[part] === undefined || obj[part] === null) {
          obj[part] = {}
        }
      }
      setValueAtPath(obj[part], pathParts.slice(1), value)
    }
  }
}

// Updated transformObject
export function transformObject(source, mapping) {
  // Deep clone so we don’t mutate the original
  const result = JSON.parse(JSON.stringify(source))
  for (const [sourcePath, targetPath] of Object.entries(mapping)) {
    const sourceValue = getValueAtPath(source, splitPath(sourcePath))
    // If sourceValue is explicitly undefined, we skip setting it.
    if (sourceValue !== undefined) {
      setValueAtPath(result, splitPath(targetPath), sourceValue)
    }
    // Remove original only if it was moved (and it wasn’t a null leaf).
    if (sourcePath !== targetPath) {
      deleteValueAtPath(result, splitPath(sourcePath))
    }
  }

  // Remove keys that are undefined or empty objects/arrays; null stays intact.
  removeUndefinedProperties(result)
  return result
}

// Updated removeUndefinedProperties
export function removeUndefinedProperties(obj) {
  if (Array.isArray(obj)) {
    for (let i = obj.length - 1; i >= 0; i--) {
      const val = obj[i]
      if (typeof val === 'object' && val !== null) {
        removeUndefinedProperties(val)
        // If that nested object/array is now empty, remove it.
        // Note: `{ a: null }` is NOT “empty” because it has a key—so null is preserved.
        if (isEmpty(val)) {
          obj.splice(i, 1)
        }
      } else if (val === undefined) {
        obj.splice(i, 1)
      }
      // If val is explicitly null, we leave it as-is.
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const key of Object.keys(obj)) {
      const val = obj[key]
      if (typeof val === 'object' && val !== null) {
        removeUndefinedProperties(val)
        // If now empty, delete it. But if val was `{ something: null }`, it’s not empty.
        if (isEmpty(val)) {
          delete obj[key]
        }
      } else if (val === undefined) {
        delete obj[key]
      }
      // If val is null, we do nothing—null stays in place.
    }
  }
}

// Helper function to check if an object or array is empty
function isEmpty(obj) {
  if (Array.isArray(obj)) {
    return obj.length === 0
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).length === 0
  }
  return false
}

function splitPath(path) {
  return path.split('.').reduce((acc, part) => {
    const arrayParts = part.split('[]')
    arrayParts.forEach((p, index) => {
      if (p) acc.push(p)
      if (index < arrayParts.length - 1) acc.push('[]')
    })
    return acc
  }, [])
}

// Updated deleteValueAtPath
function deleteValueAtPath(obj, pathParts) {
  if (obj === undefined) return
  // If we hit a `null` midway, we just stop—there’s nothing more to delete.
  if (obj === null) return
  if (pathParts.length === 0) return

  const part = pathParts[0]

  if (part === '[]') {
    if (!Array.isArray(obj)) return
    for (const item of obj) {
      deleteValueAtPath(item, pathParts.slice(1))
    }
  } else {
    if (pathParts.length === 1) {
      if (Array.isArray(obj)) {
        for (const item of obj) {
          if (item && typeof item === 'object') {
            delete item[part]
          }
        }
      } else if (obj && typeof obj === 'object') {
        delete obj[part]
      }
    } else {
      const next = obj[part]
      if (Array.isArray(next)) {
        for (const item of next) {
          deleteValueAtPath(item, pathParts.slice(1))
        }
      } else if (next !== undefined && next !== null) {
        deleteValueAtPath(next, pathParts.slice(1))
      }
    }
  }
}
